/**
 * initializeEffect — populates the CanvasState with the right
 * particles / ribbons / fragments / trail canvas for each effect
 * code. Static data only — no animation frame logic.
 */
import type { CanvasEffectId, CanvasState } from '../types';
import { makeParticle } from '../types';
import { choose, heartPoint, random, softRandom } from '../utils';

/** Used by the sparkler effect to seed its sparks array. */
export function spawnSparkHearts(
  state: CanvasState,
  x: number,
  y: number,
  count: number,
): void {
  const colors = ['#FFF5E6', '#FFBF00', '#FF6B6B'];
  for (let i = 0; i < count; i += 1) {
    if (state.sparks.length > 190) {
      state.sparks.shift();
    }
    const angle = random(0, Math.PI * 2);
    const speed = random(0.8, 3.2);
    const life = random(22, 38);
    state.sparks.push(
      makeParticle({
        x: x + random(-5, 5),
        y: y + random(-5, 5),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - random(0.2, 1.2),
        size: random(3, 9),
        alpha: 1,
        color: choose(colors),
        life,
        maxLife: life,
        rot: random(-Math.PI, Math.PI),
        vr: random(-0.08, 0.08),
      }),
    );
  }
}

export function spawnBubbleSplash(
  state: CanvasState,
  x: number,
  y: number,
  color: string,
): void {
  if (state.sparks.length > 80) return;
  for (let i = 0; i < 3; i += 1) {
    state.sparks.push(
      makeParticle({
        x,
        y,
        vx: random(-0.6, 0.6),
        vy: random(-0.8, 0.25),
        size: random(1.2, 3.2),
        color,
        life: random(18, 32),
        maxLife: 32,
      }),
    );
  }
}

export function spawnFlowerBurst(
  state: CanvasState,
  x: number,
  y: number,
  count: number,
): void {
  const colors = ['#FFFFFF', '#FFD93D', '#FF6B6B'];
  for (let i = 0; i < count; i += 1) {
    const angle = (i / count) * Math.PI * 2 + random(-0.2, 0.2);
    const speed = random(0.8, 2.2);
    state.blooms.push(
      makeParticle({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: random(5, 10),
        color: choose(colors),
        life: random(28, 48),
        maxLife: 48,
        rot: angle,
        vr: random(-0.04, 0.04),
        phase: random(0, Math.PI * 2),
      }),
    );
  }
}

export function initializeEffect(
  effect: CanvasEffectId,
  state: CanvasState,
  reducedMotion: boolean,
): void {
  const { width: w, height: h } = state;
  const motionScale = reducedMotion ? 0.38 : 1;
  state.particles = [];
  state.sparks = [];
  state.blooms = [];
  state.ribbons = [];
  state.fragments = [];
  state.trailCanvas = null;
  state.trailCtx = null;
  state.lastSpawn = 0;

  switch (effect) {
    case 'floating-heart-bokeh': {
      const colors = ['#FFB6C1', '#DCAE96', '#C9A96E'];
      for (let i = 0; i < Math.floor(92 * motionScale); i += 1) {
        const size = random(8, 58);
        state.particles.push(
          makeParticle({
            x: random(-w * 0.1, w * 1.1),
            y: random(-h * 0.05, h * 1.12),
            vx: random(-0.12, 0.12),
            vy: -random(0.18, 0.72) * (62 / (size + 40)),
            size,
            alpha: random(0.12, 0.58),
            color: choose(colors),
            blur: random(0, 6.5),
            depth: random(0.2, 1),
            phase: random(0, Math.PI * 2),
          }),
        );
      }
      break;
    }
    case 'rose-petal-cascade': {
      const colors = ['#8B0000', '#800020', '#FADADD', '#B64056'];
      for (let i = 0; i < Math.floor(78 * motionScale); i += 1) {
        state.particles.push(
          makeParticle({
            x: random(-w * 0.08, w * 1.08),
            y: random(-h, h * 0.95),
            vx: random(-0.35, 0.35),
            vy: random(0.34, 1.25),
            size: random(10, 24),
            alpha: random(0.5, 0.95),
            color: choose(colors),
            rot: random(-Math.PI, Math.PI),
            vr: random(-0.035, 0.035),
            phase: random(0, 100),
          }),
        );
      }
      break;
    }
    case 'sparkler-trail-hearts': {
      for (let i = 0; i < Math.floor(24 * motionScale); i += 1) {
        spawnSparkHearts(state, w * 0.5 + random(-40, 40), h * 0.52 + random(-70, 70), 1);
      }
      break;
    }
    case 'constellation-connection': {
      for (let i = 0; i < Math.floor(82 * motionScale); i += 1) {
        const onHeart = i < 34;
        const point = heartPoint((i / 34) * Math.PI * 2);
        state.particles.push(
          makeParticle({
            x: onHeart
              ? w * 0.5 + point.x * w * 0.21 + random(-26, 26)
              : random(0, w),
            y: onHeart
              ? h * 0.42 - point.y * h * 0.16 + random(-22, 22)
              : random(0, h),
            vx: random(-0.16, 0.16),
            vy: random(-0.12, 0.12),
            size: onHeart ? random(1.3, 2.4) : random(0.8, 2.1),
            alpha: random(0.5, 1),
            color: onHeart ? '#D4AF37' : '#E6E6FA',
            baseX: onHeart ? w * 0.5 + point.x * w * 0.21 : random(0, w),
            baseY: onHeart ? h * 0.42 - point.y * h * 0.16 : random(0, h),
            depth: onHeart ? 1 : 0,
            phase: random(0, Math.PI * 2),
          }),
        );
      }
      break;
    }
    case 'velvet-dust-motes': {
      for (let i = 0; i < Math.floor(190 * motionScale); i += 1) {
        state.particles.push(
          makeParticle({
            x: random(0, w),
            y: random(0, h),
            vx: random(-0.045, 0.045),
            vy: random(-0.035, 0.045),
            size: random(0.45, 1.8),
            alpha: random(0.1, 0.8),
            color: choose(['#FFF8E7', '#F0E68C']),
            phase: random(0, Math.PI * 2),
          }),
        );
      }
      break;
    }
    case 'heartbeat-pulse-rings':
      break;
    case 'champagne-bubble-rise': {
      const colors = ['#F7E7CE', '#B76E79', '#FFFFFF'];
      for (let i = 0; i < Math.floor(112 * motionScale); i += 1) {
        const size = random(2, 11);
        state.particles.push(
          makeParticle({
            x: random(w * 0.08, w * 0.92),
            y: random(0, h * 1.08),
            vx: 0,
            vy: random(0.45, 1.45) * (1 + size / 12),
            size,
            alpha: random(0.22, 0.82),
            color: choose(colors),
            baseX: random(w * 0.08, w * 0.92),
            phase: random(0, Math.PI * 2),
          }),
        );
      }
      break;
    }
    case 'firefly-courtship': {
      for (let i = 0; i < Math.floor(48 * motionScale); i += 1) {
        state.particles.push(
          makeParticle({
            x: random(w * 0.08, w * 0.92),
            y: random(h * 0.12, h * 0.84),
            vx: random(-0.2, 0.2),
            vy: random(-0.16, 0.16),
            size: random(2, 4.6),
            alpha: random(0.45, 1),
            color: choose(['#CCFF00', '#FFD700']),
            phase: random(0, Math.PI * 2),
            seed: random(0, 100),
          }),
        );
      }
      break;
    }
    case 'silk-ribbon-flow': {
      const pairs = [
        ['#F1E3D3', '#E6B8B8'],
        ['#E6B8B8', '#C9A96E'],
        ['#FFF2E4', '#D7A0B5'],
      ];
      pairs.forEach(([colorA, colorB], index) => {
        const points = [];
        for (let i = 0; i < 30; i += 1) {
          const u = i / 29;
          points.push(
            makeParticle({
              x: w * (u * 1.2 - 0.1),
              y: h * (0.38 + index * 0.09 + Math.sin(u * Math.PI * 2) * 0.05),
              size: 1,
              phase: random(0, Math.PI * 2),
            }),
          );
        }
        state.ribbons.push({
          points,
          colorA,
          colorB,
          phase: index * 1.7,
          width: 12 + index * 4,
        });
      });
      break;
    }
    case 'diamond-dust-sparkle': {
      const colors = ['#FFFFFF', '#FF69B4', '#B0E0E6'];
      for (let i = 0; i < Math.floor(148 * motionScale); i += 1) {
        const clustered = Math.random() < 0.6;
        state.particles.push(
          makeParticle({
            x: clustered ? softRandom(w * 0.5, w * 0.26) : random(0, w),
            y: clustered ? softRandom(h * 0.43, h * 0.26) : random(0, h),
            vx: random(-0.04, 0.04),
            vy: random(-0.035, 0.035),
            size: random(1.4, 4.4),
            alpha: random(0.35, 1),
            color: choose(colors),
            rot: random(0, Math.PI),
            vr: random(-0.03, 0.03),
            phase: random(0, Math.PI * 2),
            depth: clustered ? 1 : 0,
          }),
        );
      }
      break;
    }
    case 'pheromone-trail': {
      state.trailCanvas = document.createElement('canvas');
      state.trailCanvas.width = w;
      state.trailCanvas.height = h;
      state.trailCtx = state.trailCanvas.getContext('2d');
      for (let i = 0; i < Math.floor(34 * motionScale); i += 1) {
        state.particles.push(
          makeParticle({
            x: random(w * 0.08, w * 0.92),
            y: random(h * 0.12, h * 0.8),
            vx: random(-0.46, 0.46),
            vy: random(-0.38, 0.38),
            size: random(7, 16),
            alpha: random(0.22, 0.55),
            color: choose(['#FFB45E', '#FFDAB9', '#FF8E6E']),
            phase: random(0, Math.PI * 2),
          }),
        );
      }
      break;
    }
    case 'aurora-heart':
      break;
    case 'floating-kiss-marks': {
      const colors = ['#C41E3A', '#800020', '#D2A679', '#722F37'];
      for (let i = 0; i < Math.floor(58 * motionScale); i += 1) {
        state.particles.push(
          makeParticle({
            x: random(w * 0.08, w * 0.92),
            y: random(-h * 0.04, h * 1.1),
            vx: random(-0.12, 0.12),
            vy: -random(0.2, 0.7),
            size: random(16, 44),
            alpha: random(0.2, 0.68),
            color: choose(colors),
            rot: random(-0.38, 0.38),
            vr: random(-0.004, 0.004),
            phase: random(0, Math.PI * 2),
            blur: random(0, 1.4),
          }),
        );
      }
      break;
    }
    case 'memory-fragment-drift': {
      const colors = ['#C28762', '#7E5549', '#D6A57D', '#49606E', '#BDA88A'];
      for (let i = 0; i < Math.floor(16 * motionScale); i += 1) {
        const fw = random(w * 0.18, w * 0.28);
        state.fragments.push({
          x: random(-w * 0.2, w * 1.05),
          y: random(-h * 0.14, h * 1.08),
          vx: random(-0.18, 0.18),
          vy: random(-0.18, 0.12),
          w: fw,
          h: fw * random(1.05, 1.28),
          rot: random(-0.38, 0.38),
          vr: random(-0.003, 0.003),
          seed: random(0, 100),
          color: choose(colors),
        });
      }
      break;
    }
    case 'entwined-particle-vines': {
      for (let i = 0; i < Math.floor(130 * motionScale); i += 1) {
        const side = i % 2 === 0 ? -1 : 1;
        state.particles.push(
          makeParticle({
            x: side < 0 ? -w * 0.1 : w * 1.1,
            y: h * 0.5 + random(-h * 0.18, h * 0.18),
            vx: 0,
            vy: 0,
            size: random(1.5, 4.6),
            alpha: random(0.45, 1),
            color:
              side < 0
                ? choose(['#FF6B6B', '#FFD93D'])
                : choose(['#6BCBFF', '#A855F7']),
            depth: random(0, 1),
            phase: random(0, Math.PI * 2),
            seed: random(0, 1),
            side,
          }),
        );
      }
      break;
    }
  }
}
